import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { ArrowRight, Factory, Zap, Sparkles, Upload, Scissors, Truck, Star, Gift } from "lucide-react";

const PROMO_URL = "https://illawarrapromotions.com.au/";

// Scrolling marquee of feature taglines at the very top of the page
function FeatureMarquee() {
  const items = [
    { icon: Zap, label: "Instant online pricing", color: "#E4002B" },
    { icon: Factory, label: "Printed locally in Shellharbour", color: "#003DA5" },
    { icon: Upload, label: "Upload your artwork", color: "#111315" },
    { icon: Truck, label: "Delivery or local pickup", color: "#003DA5" },
    { icon: Scissors, label: "Standard or custom cut — no extra charge", color: "#111315" },
    { icon: Sparkles, label: "Stickers on rolls, die-cut or on sheets", color: "#E4002B" },
  ];
  const loop = [...items, ...items, ...items];
  return (
    <div className="relative overflow-hidden border-b border-[#E2DDD5] bg-white/70 backdrop-blur-sm" data-testid="kopy-feature-marquee">
      <div className="flex gap-10 whitespace-nowrap animate-marquee-left py-3">
        {loop.map((f, i) => (
          <span key={i} className="inline-flex items-center gap-2 text-[13px] font-semibold text-slate-700 shrink-0">
            <f.icon size={15} style={{ color: f.color }} />
            {f.label}
            <span className="text-slate-300 pl-2">•</span>
          </span>
        ))}
      </div>
    </div>
  );
}

export default function KopyHome() {
  return (
    <div className="App">
      <Header />
      <FeatureMarquee />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" data-testid="kopy-hero-bg">
          <img src="/products/home-hero.webp" alt="" className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0 bg-[#FAF8F5]/60 md:bg-transparent bg-gradient-to-r from-[#FAF8F5] via-[#FAF8F5]/85 to-[#FAF8F5]/10" />
        </div>

        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-14 pb-16">
          <div className="max-w-3xl">
            <div className="eyebrow mb-5" data-testid="kopy-hero-eyebrow">Shellharbour · Family owned · Commercial print</div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black leading-[0.95] tracking-tight" data-testid="kopy-hero-headline">
              Local printing<br />
              <span className="relative inline-block">
                Instant <span className="text-[#E4002B]">online</span> pricing.
                <span className="absolute left-0 right-0 -bottom-2 h-[6px] bg-[#E4002B] rounded-full" aria-hidden="true" />
              </span>
            </h1>
            <p className="mt-8 text-lg text-slate-700 max-w-xl">
              From custom stickers to professional corflute and foamboard signage — build your order online and see your price instantly. Printed locally in Shellharbour.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/stickers" className="btn-primary" style={{ background: "#E4002B" }} data-testid="kopy-hero-cta-stickers">
                Shop Stickers <ArrowRight size={16}/>
              </Link>
              <Link to="/signs" className="btn-primary" style={{ background: "#003DA5" }} data-testid="kopy-hero-cta-signs">
                Shop Signs <ArrowRight size={16}/>
              </Link>
              <a href={PROMO_URL} className="btn-primary" style={{ background: "#2B4590" }} data-testid="kopy-hero-cta-promo">
                Shop Promo <ArrowRight size={16}/>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* DUAL CHOICE — matching style, differentiated by accent */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-10 pb-16" data-testid="kopy-choice-grid">
        <div className="text-center mb-10">
          <div className="eyebrow mb-2">Where would you like to start?</div>
          <h2 className="font-display text-3xl sm:text-4xl font-black">Three product ranges. One local team.</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          <ChoiceCard
            to="/stickers"
            logo="/brand/kwik-stickers.png"
            logoAlt="Kwik Stickers"
            accent="#E4002B"
            eyebrow="Rolls, die-cut & sheet stickers"
            title="Order custom printed stickers."
            body="Stickers on rolls, die-cut stickers or stickers on sheets. Upload your own artwork — full-colour, printed locally."
            chips={["Rolls", "Die-cut", "Sheets"]}
            cta="Shop Kwik Stickers"
            icon={<Sparkles size={220}/>}
            testid="kopy-choice-stickers"
          />
          <ChoiceCard
            to="/signs"
            logo="/brand/kwik-signs.png"
            logoAlt="Kwik Signs"
            accent="#003DA5"
            eyebrow="Corflute signs & foamboard"
            title="Order custom signs & foamboard."
            body="Full-colour 5 mm corflute and foamboard. Single or double-sided, standard or custom cut."
            chips={["A4 to A0", "Single or double-sided", "Custom cut"]}
            cta="Shop Kwik Signs"
            icon={<Factory size={220}/>}
            testid="kopy-choice-signs"
          />
          <ChoiceCard
            href={PROMO_URL}
            logo="/brand/kwik-promo.png"
            logoAlt="Kwik Promo"
            accent="#2B4590"
            eyebrow="Branded promotional products"
            title="Order promotional products."
            body="Branded merchandise, giveaways and promotional products — browse our full range at Illawarra Promotions."
            chips={["Merchandise", "Giveaways", "Corporate gifts"]}
            cta="Shop Kwik Promo"
            icon={<Gift size={220}/>}
            testid="kopy-choice-promo"
          />
        </div>
      </section>

      {/* FEATURE STRIP */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: Upload, label: "Upload your artwork", sub: "PDF · PNG · SVG · AI · EPS" },
            { icon: Zap, label: "Live pricing", sub: "Instant quote as you build" },
            { icon: Scissors, label: "Standard or custom cut", sub: "No extra charge" },
            { icon: Truck, label: "Delivery across Australia", sub: "Or local pickup" },
          ].map((f) => (
            <div key={f.label} className="sf-card p-5 flex flex-col gap-2">
              <f.icon size={22} className="text-[#111315]" />
              <div className="font-display font-bold text-base leading-tight">{f.label}</div>
              <p className="text-xs text-slate-500">{f.sub}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAMILY STRIP */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-16">
        <div className="sf-card p-8 lg:p-12 grid md:grid-cols-12 gap-6 items-center">
          <div className="md:col-span-7">
            <div className="eyebrow">One business. Three brands.</div>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold mt-2 leading-tight">Family owned. Locally produced.</h2>
            <p className="mt-4 text-slate-700 max-w-xl">
              Kwik Stickers, Kwik Signs and Kwik Promo are all proudly produced by the team at Kwik Kopy Shellharbour.
              One cart, one checkout, one friendly local team behind every order.
            </p>
            <div className="mt-5 flex items-center gap-2 text-xs">
              <div className="flex items-center gap-1 text-[#FFB800]">
                <Star size={14} fill="currentColor" />
                <Star size={14} fill="currentColor" />
                <Star size={14} fill="currentColor" />
                <Star size={14} fill="currentColor" />
                <Star size={14} fill="currentColor" />
              </div>
              <span className="text-slate-500">Trusted by local businesses across the Illawarra.</span>
            </div>
          </div>
          <div className="md:col-span-5 grid grid-cols-3 gap-3">
            <div className="rounded-2xl p-4 text-center bg-[#F3EFEA] border border-[#E2DDD5]"><div className="font-display font-black text-xl text-[#E4002B]">Local</div><div className="text-[10px] text-slate-500 mt-1 uppercase tracking-widest">Shellharbour</div></div>
            <div className="rounded-2xl p-4 text-center bg-[#F3EFEA] border border-[#E2DDD5]"><div className="font-display font-black text-xl text-[#111315]">Family</div><div className="text-[10px] text-slate-500 mt-1 uppercase tracking-widest">Owned</div></div>
            <div className="rounded-2xl p-4 text-center bg-[#F3EFEA] border border-[#E2DDD5]"><div className="font-display font-black text-xl text-[#003DA5]">Fast</div><div className="text-[10px] text-slate-500 mt-1 uppercase tracking-widest">Made to order</div></div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS CTA */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-24 text-center">
        <h3 className="font-display text-2xl sm:text-3xl font-extrabold">New here?</h3>
        <p className="text-slate-600 mt-2">See how a Kwik Kopy order comes together in four simple steps.</p>
        <Link to="/how-it-works" className="btn-outline mt-5 inline-flex" data-testid="kopy-how-it-works-cta"><Zap size={16}/> How It Works</Link>
      </section>

      <Footer />
    </div>
  );
}

function ChoiceCard({ to, href, logo, logoAlt, accent, eyebrow, title, body, chips, cta, icon, testid }) {
  const Tag = href ? "a" : Link;
  return (
    <Tag
      {...(href ? { href } : { to })}
      data-testid={testid}
      className="sf-card p-8 lg:p-10 flex flex-col gap-5 relative overflow-hidden group transition"
      style={{ borderTop: `6px solid ${accent}` }}
    >
      <div className="h-14 flex items-center">
        <img src={logo} alt={logoAlt} className="h-full w-auto max-w-[240px] object-contain object-left"/>
      </div>
      <div className="font-mono-brand text-[11px] font-bold uppercase tracking-[0.22em]" style={{ color: accent }}>{eyebrow}</div>
      <h3 className="font-display text-2xl sm:text-3xl font-extrabold leading-tight">{title}</h3>
      <p className="text-slate-600 max-w-md">{body}</p>
      <div className="flex flex-wrap gap-2 text-xs">
        {chips.map((c) => <span key={c} className="chip">{c}</span>)}
      </div>
      <div className="mt-auto pt-4">
        <span className="btn-primary" style={{ background: accent }}>{cta} <ArrowRight size={16} /></span>
      </div>
      <div className="absolute -right-5 -bottom-5 opacity-[0.06]" style={{ color: accent }}>
        {icon}
      </div>
    </Tag>
  );
}
